import { combineReducers } from 'redux';
import { getFormValues } from 'redux-form';
import { push } from 'connected-react-router';
import axios from 'axios';
import humps from 'humps';
import _ from 'lodash';
import queryString from 'query-string';

import * as Urls from 'src/constants/EndpointUrls';
import {
  REPORT_LABOR_INSURANCE_SUMMARY_FORM,
  REPORT_LABOR_INSURANCE_DECLARATION_FORM,
  REPORT_LABOR_INSURANCE_DECLARATION_INSURANCE_RATE_FORM
} from 'src/constants/FormNames';
import { concatParamsToUrl, excludeValuesIfNotDefined } from 'src/utils/Http';
import { saveCurrentQueries } from 'src/QueriesStorageApi';
import createNamedWrapperReducer from 'src/reducers/createNamedWrapperReducer';
import { listReducer, itemReducer } from 'src/reducers/common';
import {
  newItemFailure,
  fetchItem,
  fetchItemSuccess,
  fetchItemFailure,
  editItemFailure,
  setItem,
  setItemExtra,
  getItem,
  getItemErrors
} from 'src/reducers/common/itemReducer';
import {
  getListQueries,
  fetchList,
  fetchListSuccess,
  fetchListFailure,
  setExtras
} from 'src/reducers/common/listReducer';

export const REDUCER_NAME = 'reportLaborInsurances';
export const clearErrors = () => dispatch => dispatch(newItemFailure(REDUCER_NAME, []));
export const setErrors = errors => dispatch => dispatch(newItemFailure(REDUCER_NAME, errors));

export const setSummaryTableShowMode = actionMode => async dispatch => {
  dispatch(setExtras(REDUCER_NAME, { summaryTableShowMode: actionMode }));
};

export const setDeclarationFormShowMode = actionMode => async dispatch => {
  dispatch(setItemExtra(REDUCER_NAME, { declarationFormActionMode: actionMode }));
};

export const transformMonthlySummariesToFormValues = state =>
  state[REDUCER_NAME].list.data.monthlySummaries &&
  state[REDUCER_NAME].list.data.monthlySummaries.reduce(
    (pre, monthlySummary) => ({ monthlySummaries: { ...pre.monthlySummaries, [monthlySummary.id]: monthlySummary } }),
    { monthlySummaries: {} }
  );

// Async Action Creators
export const fetchReportLaborInsurances = () => async (dispatch, getState) => {
  try {
    dispatch(fetchList(REDUCER_NAME));
    let queries = getListQueries(REDUCER_NAME, getState());
    queries = excludeValuesIfNotDefined(queries);
    saveCurrentQueries(queries, REDUCER_NAME);

    const response = await axios.get(Urls.REPORT_LABOR_INSURANCE_SUMMARIES_SEARCH, {
      params: humps.decamelizeKeys(queries)
    });
    const payload = {
      data: {
        monthlySummaries: response.data.payload.monthlySummaries,
        yearlySummary: response.data.payload.yearlySummary
      }
    };
    dispatch(fetchListSuccess(REDUCER_NAME, payload));
  } catch (exception) {
    dispatch(fetchListFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const editMonthlySummary = laborInsuranceYear => async (dispatch, getState) => {
  try {
    const state = getState();
    const monthlySummaries = _.get(getFormValues(REPORT_LABOR_INSURANCE_SUMMARY_FORM)(state), 'monthlySummaries');

    const httpBody = {
      laborInsuranceYear,
      monthlySummaries
    };
    await axios.put(Urls.REPORT_LABOR_INSURANCE_SUMMARIES_UPDATE_SUMMARIES, httpBody);

    window.location.reload();
  } catch (exception) {
    dispatch(setExtras(REDUCER_NAME, { editMonthlySummaryErrors: exception.response.data.errors.messages }));
  }
};

export const clearMonthlySummaryErrors = () => async dispatch => {
  dispatch(setExtras(REDUCER_NAME, { editMonthlySummaryErrors: [] }));
};

// Async Action Creators
export const fetchReportLaborInsuranceDeclarationForm = () => async (dispatch, getState) => {
  try {
    dispatch(fetchItem(REDUCER_NAME));
    let queries = getListQueries(REDUCER_NAME, getState());
    queries = excludeValuesIfNotDefined(queries);
    saveCurrentQueries(queries, REDUCER_NAME);

    const response = await axios.get(Urls.REPORT_LABOR_INSURANCE_DECLARATIONS_SEARCH, {
      params: humps.decamelizeKeys(queries)
    });
    dispatch(fetchItemSuccess(REDUCER_NAME, response.data.payload.declarationForm));
  } catch (exception) {
    dispatch(fetchItemFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const editDeclarationForm = laborInsuranceYear => async (dispatch, getState) => {
  try {
    const state = getState();
    const declarationForm = getFormValues(REPORT_LABOR_INSURANCE_DECLARATION_FORM)(state);

    const httpBody = {
      laborInsuranceYear,
      declarationForm
    };
    await axios.put(Urls.REPORT_LABOR_INSURANCE_DECLARATIONS_UPDATE_DECLARATIONS, httpBody);

    window.location.reload();
  } catch (exception) {
    dispatch(setItemExtra(REDUCER_NAME, { editDeclarationFormErrors: exception.response.data.errors.messages }));
  }
};

export const clearDeclarationFormErrors = () => async dispatch => {
  dispatch(setItemExtra(REDUCER_NAME, { editDeclarationFormErrors: [] }));
};

export const updateLaborInsuranceRates = ownProps => async (dispatch, getState) => {
  try {
    const state = getState();
    const insuranceRates = getFormValues(REPORT_LABOR_INSURANCE_DECLARATION_INSURANCE_RATE_FORM)(state);

    const params = { labor_insurance_year: ownProps.laborInsuranceYear, ...humps.decamelizeKeys(insuranceRates) };
    const updateUrl = concatParamsToUrl(Urls.REPORT_LABOR_INSURANCE_DECLARATIONS_UPDATE_INSURANCE_RATES, { params });
    await axios.put(updateUrl, params);

    const newInsuranceRate = {
      confirmedInsuranceFeeRateForAccidentInsurance: insuranceRates.confirmedAccidentInsuranceRate,
      confirmedInsuranceFeeRateForCalculationTargetOfEmploymentInsurance:
        insuranceRates.confirmedEmploymentInsuranceRate,
      confirmedInsuranceFeeRateForAgedEmploymentInsurance: insuranceRates.confirmedEmploymentInsuranceRate,
      confirmedInsuranceFeeRateForLaborInsurance:
        _.toInteger(insuranceRates.confirmedAccidentInsuranceRate) +
        _.toInteger(insuranceRates.confirmedEmploymentInsuranceRate),
      estimateInsuranceFeeRateForAccidentInsurance: insuranceRates.estimateAccidentInsuranceRate,
      estimateInsuranceFeeRateForCalculationTargetOfEmploymentInsurance: insuranceRates.estimateEmploymentInsuranceRate,
      estimateInsuranceFeeRateForLaborInsurance:
        _.toInteger(insuranceRates.estimateAccidentInsuranceRate) +
        _.toInteger(insuranceRates.estimateEmploymentInsuranceRate)
    };

    const newParams = { ...getItem(REDUCER_NAME, state), ...newInsuranceRate };
    dispatch(setItem(REDUCER_NAME, newParams));
    ownProps.allCalculations(newParams);
    ownProps.cancelBtnClick();
  } catch (exception) {
    dispatch(setItemExtra(REDUCER_NAME, { updateLaborInsuranceRateErrors: exception.response.data.errors.messages }));
  }
};

export const clearLaborInsuranceRateErrors = () => async dispatch => {
  dispatch(setItemExtra(REDUCER_NAME, { updateLaborInsuranceRateErrors: [] }));
};

export const getLaborInsuranceRateErrors = state => {
  const errors = getItemErrors(REDUCER_NAME, state) || [{}];
  return (errors[0] || {}).updateLaborInsuranceRateErrors || [];
};

export const recalculateReportLaborInsuranceDeclarationForm = ownProps => async dispatch => {
  try {
    const params = { labor_insurance_year: ownProps.laborInsuranceYear };
    const recalculateUrl = concatParamsToUrl(Urls.REPORT_LABOR_INSURANCE_DECLARATIONS_RECALCULATE, { params });
    await axios.put(recalculateUrl, params);

    window.location.reload();
  } catch (exception) {
    dispatch(editItemFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const recalculateReportLaborInsuranceSummaryList = ownProps => async dispatch => {
  try {
    const params = { labor_insurance_year: ownProps.laborInsuranceYear };
    const recalculateUrl = concatParamsToUrl(Urls.REPORT_LABOR_INSURANCE_SUMMARIES_RECALCULATE, { params });
    await axios.put(recalculateUrl, params);

    window.location.reload();
  } catch (exception) {
    dispatch(editItemFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const getRecalculateReportLaborInsuranceDeclarationFormErrors = state => {
  const errors = getItemErrors(REDUCER_NAME, state) || [{}];
  return (errors[0] || {}).recalculateReportLaborInsuranceDeclarationFormErrors || [];
};

export const getRecalculateReportLaborInsuranceSummaryListErrors = state => {
  const errors = getItemErrors(REDUCER_NAME, state) || [{}];
  return (errors[0] || {}).recalculateReportLaborInsuranceSummaryListErrors || [];
};

export const changeUrl = (laborInsuranceYear, summaryType) => async (dispatch, getState) => {
  const state = getState();
  const url = summaryType === '2' ? Urls.REPORT_LABOR_INSURANCE_DECLARATIONS : Urls.REPORT_LABOR_INSURANCE_SUMMARIES;
  const search = queryString.stringify({ labor_insurance_year: laborInsuranceYear });
  const location = { ...state.router.location, pathname: url };
  dispatch(push({ ...location, search }));
};

export default combineReducers({
  list: createNamedWrapperReducer(listReducer, REDUCER_NAME),
  item: createNamedWrapperReducer(itemReducer, REDUCER_NAME)
});
